import { z } from 'zod';
import type { operations } from '@molchanovs/api-contract';
import { api } from './client';
import { ApiError } from './errors';
import {
  getBubbleMutationOperation,
  bubbleMutationOperationReceiptSchema,
} from './bubble-mutation-operation';

export type HelpdeskAiContentKind = 'prompts' | 'instructions';
export type AiContentListQuery = NonNullable<operations['getApiV1Prompts']['parameters']['query']>;
export type AiContentListResponse =
  operations['getApiV1Prompts']['responses'][200]['content']['application/json'];
export type AiContentResponse =
  operations['getApiV1PromptsByPromptId']['responses'][200]['content']['application/json'];
export type AiContentListItem = AiContentListResponse['items'][number];
export type AiContentDetailResponse = AiContentResponse & { etag: string };
export type AiContentPatch = NonNullable<
  operations['patchApiV1PromptsByPromptId']['requestBody']
>['content']['application/json'];
type AiContentSaveBody =
  operations['patchApiV1PromptsByPromptId']['responses'][200]['content']['application/json'];
export type AiContentCreateBody = NonNullable<
  operations['postApiV1Prompts']['requestBody']
>['content']['application/json'];
export type AiContentFormOptions =
  operations['getApiV1PromptsFormOptions']['responses'][200]['content']['application/json'];
export type AiContentQueuedResponse =
  operations['postApiV1Prompts']['responses'][201]['content']['application/json'] & {
    /** Transport receipt from response headers, never part of the JSON wire body. */
    operation: z.infer<typeof bubbleMutationOperationReceiptSchema>;
  };
export type AiContentSaveResponse =
  | Extract<AiContentSaveBody, { meta: unknown }>
  | AiContentQueuedResponse;
export type AiContentDeleteResponse =
  operations['deleteApiV1PromptsByPromptId']['responses'][202]['content']['application/json'];
const id = z.uuid();
const etagSchema = z.string().regex(/^"[a-f0-9]{64}"$/u);
const itemSchema = z.object({
  id,
  title: z.string().nullable(),
  type: z.string().nullable(),
  updatedAt: z.string(),
  deletion: z.object({ state: z.enum(['pending', 'failed']), operationId: id }).nullable(),
  creation: z.object({ state: z.enum(['pending', 'failed']), operationId: id }).nullable(),
});
const listSchema: z.ZodType<AiContentListResponse> = z.object({
  items: z.array(itemSchema),
  meta: z.object({
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    totalItems: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
  }),
});
const detailSchema: z.ZodType<AiContentResponse> = z.object({
  data: itemSchema.extend({ content: z.string().nullable() }),
});
const directSaveSchema = z.object({
  data: z.object({ id }),
  meta: z.object({
    localCommitted: z.literal(true),
    operationId: id,
    replayed: z.boolean(),
    mirror: z.object({
      state: z.enum(['confirmed', 'failed', 'unknown']),
      code: z.enum(['PROVIDER_REJECTED', 'OUTCOME_UNKNOWN']).nullable(),
    }),
  }),
});
const writeSchema = z.object({ data: z.object({ id }) });
// Dispatch on the known discriminator before parsing: an invalid legacy meta must
// not be stripped and accidentally accepted as an ordinary queued response.
const saveSchema: z.ZodType<AiContentSaveBody> = z.preprocess(
  (value) => value,
  z.unknown().transform((value, ctx) => {
    const schema =
      value && typeof value === 'object' && 'meta' in value ? directSaveSchema : writeSchema;
    const result = schema.safeParse(value);
    if (!result.success) {
      for (const issue of result.error.issues) ctx.addIssue({ ...issue });
      return z.NEVER;
    }
    return result.data;
  })
);
const optionsSchema: z.ZodType<AiContentFormOptions> = z.object({
  types: z.array(z.object({ value: z.string().min(1), label: z.string() })),
});
const createSchema = z
  .object({
    title: z
      .string()
      .trim()
      .min(1)
      .max(500)
      .refine((value) => !value.includes('\u0000')),
    content: z
      .string()
      .min(1)
      .max(100000)
      .refine((value) => !value.includes('\u0000')),
    type: z.string().min(1).nullable().optional(),
  })
  .strict();
const deleteSchema: z.ZodType<AiContentDeleteResponse> = z.object({
  data: z.object({
    operationId: id,
    status: z.literal('accepted'),
    monitorUrl: z.string().startsWith('/api/v1/operations/'),
  }),
});
const patchSchema: z.ZodType<AiContentPatch> = z
  .object({
    type: z.string().min(1).nullable().optional(),
    title: z
      .string()
      .trim()
      .min(1)
      .max(500)
      .refine((v) => !v.includes('\u0000'))
      .optional(),
    content: z
      .string()
      .min(1)
      .max(100000)
      .refine((v) => !v.includes('\u0000'))
      .optional(),
  })
  .strict()
  .refine((v) => v.title !== undefined || v.content !== undefined || v.type !== undefined);
const querySchema: z.ZodType<AiContentListQuery> = z
  .object({
    page: z.number().int().min(1).max(100000).optional(),
    pageSize: z.number().int().min(1).max(100).optional(),
    q: z
      .string()
      .trim()
      .max(200)
      .refine((value) => !value.includes('\u0000'))
      .optional(),
  })
  .strict();
function input<T>(schema: z.ZodType<T>, value: unknown): T {
  const parsed = schema.safeParse(value);
  if (!parsed.success)
    throw new ApiError({
      status: 0,
      code: 'VALIDATION_ERROR',
      message: 'Invalid AI content request.',
      details: parsed.error.issues,
    });
  return parsed.data;
}
function path(kind: HelpdeskAiContentKind) {
  return kind === 'prompts' ? '/api/v1/prompts' : '/api/v1/assistant-instructions';
}
function operationReceipt(headers: Headers, status: number) {
  const replayed = headers.get('x-operation-replayed');
  const receipt = bubbleMutationOperationReceiptSchema.safeParse({
    operationId: headers.get('x-operation-id'),
    operationStatus: headers.get('x-operation-status'),
    replayed: replayed === 'true' ? true : replayed === 'false' ? false : undefined,
    statusUrl: headers.get('x-operation-status-url'),
    resultUrl: headers.get('x-operation-result-url'),
  });
  if (!receipt.success)
    throw new ApiError({
      status,
      code: 'VALIDATION_ERROR',
      message: 'AI content response requires an operation receipt.',
    });
  return receipt.data;
}
export const helpdeskAiContentApi = {
  operation: getBubbleMutationOperation,
  async formOptions(
    kind: HelpdeskAiContentKind,
    signal?: AbortSignal
  ): Promise<AiContentFormOptions> {
    return api.get(`${path(kind)}/form-options`, { signal, schema: optionsSchema });
  },
  async create(
    kind: HelpdeskAiContentKind,
    body: AiContentCreateBody,
    key: string
  ): Promise<AiContentQueuedResponse> {
    const schema =
      kind === 'instructions' ? createSchema.extend({ type: z.string().min(1) }) : createSchema;
    const response = await api.postWithMetadata(path(kind), input(schema, body), {
      headers: { 'Idempotency-Key': input(z.string().min(8).max(200), key) },
      schema: writeSchema,
    });
    return { ...response.data, operation: operationReceipt(response.headers, response.status) };
  },
  async delete(
    kind: HelpdeskAiContentKind,
    recordId: string,
    etag: string,
    key: string
  ): Promise<AiContentDeleteResponse> {
    return api.delete(`${path(kind)}/${encodeURIComponent(input(id, recordId))}`, {
      headers: {
        'If-Match': input(etagSchema, etag),
        'Idempotency-Key': input(z.string().min(8).max(200), key),
      },
      schema: deleteSchema,
    });
  },
  async list(
    kind: HelpdeskAiContentKind,
    query: AiContentListQuery,
    signal?: AbortSignal
  ): Promise<AiContentListResponse> {
    return api.get(path(kind), { params: input(querySchema, query), signal, schema: listSchema });
  },
  async detail(
    kind: HelpdeskAiContentKind,
    recordId: string,
    signal?: AbortSignal
  ): Promise<AiContentDetailResponse> {
    const response = await api.getWithMetadata<AiContentResponse>(
      `${path(kind)}/${encodeURIComponent(input(id, recordId))}`,
      { signal, schema: detailSchema }
    );
    const etag = etagSchema.safeParse(response.headers.get('ETag'));
    if (!etag.success)
      throw new ApiError({
        status: response.status,
        code: 'VALIDATION_ERROR',
        message: 'AI content response requires a strong ETag.',
      });
    return { ...response.data, etag: etag.data };
  },
  async update(
    kind: HelpdeskAiContentKind,
    recordId: string,
    body: AiContentPatch,
    etag: string,
    key: string
  ): Promise<AiContentSaveResponse> {
    const response = await api.patchWithMetadata(
      `${path(kind)}/${encodeURIComponent(input(id, recordId))}`,
      input(
        patchSchema.refine((value) => kind !== 'instructions' || value.type !== null),
        body
      ),
      {
        headers: {
          'Content-Type': 'application/json',
          'If-Match': input(etagSchema, etag),
          'Idempotency-Key': input(z.string().min(8).max(200), key),
        },
        schema: saveSchema,
      }
    );
    return 'meta' in response.data
      ? response.data
      : { ...response.data, operation: operationReceipt(response.headers, response.status) };
  },
};
